/**
 * TimelineView — chapters in manuscript order with their linked cards
 * beneath, so a writer can spot pacing/chronology problems at a glance.
 * Not a react-flow canvas — a plain scrollable row.
 */

import React from 'react';
import { cx } from '@/components/site/primitives';
import { NODE_TYPE_MAP } from './constants';
import { getTimelineData } from './utils/boardHelpers';

export default function TimelineView({ story, board, onOpenNode }) {
  const timeline = getTimelineData(story, board);

  if (!timeline.length) {
    return (
      <div className="flex min-h-[240px] items-center justify-center text-sm text-subtle">
        This manuscript has no chapters yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto px-6 py-10">
      <div className="flex min-w-max items-start gap-0">
        {timeline.map(({ chapter, nodes }, i) => (
          <div key={chapter.id} className="flex w-56 shrink-0 flex-col items-center">
            <div className="flex w-full items-center">
              <span className={cx('h-px flex-1', i > 0 ? 'bg-line' : 'bg-transparent')} />
              <span className="shrink-0 rounded-full border border-line bg-card px-3.5 py-1.5 text-xs font-semibold text-ink shadow-soft">
                {chapter.title}
              </span>
              <span className={cx('h-px flex-1', i < timeline.length - 1 ? 'bg-line' : 'bg-transparent')} />
            </div>

            <span className="my-2.5 h-5 w-px bg-line" />

            <div className="flex w-full flex-col items-center gap-2">
              {nodes.length === 0 && <span className="text-xs text-subtle/60">—</span>}
              {nodes.map((node) => {
                const config = NODE_TYPE_MAP[node.type];
                return (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => onOpenNode(node.id)}
                    className="flex w-full items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-left text-xs font-medium text-ink shadow-soft transition-colors hover:border-coral/40"
                  >
                    <span className="text-sm leading-none">{config?.emoji}</span>
                    <span className="truncate">{node.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
